// Fixed ORDER-gear + SHAPE categorization for the nav drawer's dropdown
// sections. Independent of the user-editable Settings → Sidebar groups
// (sidebar_groups jsonb) — those remain a separate, freeform feature. This
// mapping is the deity roster from docs/OLMPS_V9_SPEC.md §5/§7 and
// ascension.ts's GEAR_TIERS, renamed to the drawer's section labels
// (Orient → "Objectives", Direct → "Directions"). HRMEZ and NEMOS are OLMPN
// seats *and* SHAPE sector deities (V9 §7), so they intentionally appear
// in two sections.
import { tierOf, TIER_LABEL } from './ascension';
import type { DmtToolId } from './spectrum-tools';

export interface SidebarGearGroup {
  key: string;
  label: string;
  toolIds: string[];
}

export const SIDEBAR_GEAR_GROUPS: SidebarGearGroup[] = [
  { key: 'objectives', label: 'Objectives', toolIds: ['oracle', 'telos', 'astra', 'astrs'] },
  { key: 'regulate', label: 'Regulate', toolIds: ['numa', 'moyri', 'hrmez'] },
  { key: 'directions', label: 'Directions', toolIds: ['kyrs', 'krnos', 'tkne'] },
  { key: 'execute', label: 'Execute', toolIds: ['metis', 'themis', 'jnus'] },
  { key: 'revise', label: 'Revise', toolIds: ['clio', 'argos', 'nemos', 'epic'] },
  { key: 'shape', label: 'SHAPE', toolIds: ['sofia', 'hygia', 'hypnos', 'akeso', 'plutus', 'hrmez', 'nemos'] },
];

// Items whose destination resolves to one of these toolIds stay pinned above
// the dropdown sections instead of being bucketed — SyQi is the home/
// assistant surface, not an ORDER-gear deity (V9 §2, §7).
export const SIDEBAR_PINNED_TOOL_IDS = ['syqi'];

// Per-deity ascension rank shown next to its label in the drawer (e.g.
// "KYRS - DEMI"), derived from its fixed tier slot within its ORDER gear —
// ascension.ts's GEAR_TIERS: DEMI seat (mortal) = DEMI, second (titan) = TYTN,
// third (olympian) = OLMPN. Not the user's live per-tool progress; SHAPE-only
// sector deities (SOFIA, HYGIA, HYPNOS, AKESO, PLUTUS, EPIC) aren't in the
// 15-tool ORDER roster, so they have no rank.
const RANK_ID_ALIAS: Record<string, DmtToolId> = {
  // Sidebar navigation uses 'astrs'/'jnus'; the ORDER roster spells the same
  // deities 'astra'/'janus' (see spectrum-tools.ts, ascension.ts).
  astrs: 'astra',
  jnus: 'janus',
};

export function sidebarRankLabel(toolId: string): string | null {
  const rosterId = RANK_ID_ALIAS[toolId] ?? (toolId as DmtToolId);
  const t = tierOf(rosterId);
  return t ? TIER_LABEL[t.tier] : null;
}
